import { useState, useEffect } from 'react'
import {
  FieldDesignChangeStatus,
  alphabeticalCompare,
  alphabeticalSort,
} from '@cims-online/shared'
import { FieldDesignChange } from '../../../../../FDCTrackerContext/types'
import { CIMSTags } from 'components/CIMSTags'
import { useFDCTrackerContext } from '../../../../../FDCTrackerContext'
import { CheckableGroupItem } from '../types'
import { Spin } from 'antd'
import CIMSChecklist from 'components/CIMSChecklist'

const ApprovalGroups: React.FC<FieldDesignChange> = ({
  id,
  concurrenceLevel,
  statusId,
  fdcToFDCApprovalGroups,
  resolution,
  deleted,
}) => {
  const {
    metadata,
    mutations: { approve },
    fieldDesignChanges,
  } = useFDCTrackerContext()
  const [groups, setGroups] = useState<CheckableGroupItem[]>([])
  useEffect(() => {
    // for closed fdcs, use the join table
    if (statusId === FieldDesignChangeStatus.Closed.id)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
      setGroups(
        fdcToFDCApprovalGroups
          .sort((a, b) =>
            alphabeticalCompare(a.approvalGroup, b.approvalGroup, 'name')
          )
          .reduce<CheckableGroupItem[]>(
            (
              items,
              {
                approvalGroup: { deleted, id, name, description },
                approved,
                approvalBy: { displayName, mail },
              }
            ) => {
              if (!deleted)
                items.push({
                  id,
                  name,
                  description,
                  checked: approved,
                  by: displayName ?? mail,
                })
              return items
            },
            []
          )
      )
    else
      setGroups(
        (metadata.fdcApprovalGroups ?? [])
          .filter(({ concurrenceLevels }) =>
            concurrenceLevels.some(({ id }) => id === concurrenceLevel?.id)
          )
          .sort(alphabeticalSort('name'))
          .map(({ id, name, description }) => {
            const fdc2group = fdcToFDCApprovalGroups.find(
              ({ approvalGroup }) => approvalGroup.id === id
            )
            const checked = fdc2group?.approved ?? false
            const by =
              fdc2group?.approvalBy?.displayName ?? fdc2group?.approvalBy?.mail
            return { id, name, description, checked, by }
          })
      )
    return () => setGroups([])
  }, [
    metadata?.fdcApprovalGroups,
    statusId,
    fdcToFDCApprovalGroups,
    concurrenceLevel?.id,
  ])
  const renderTags =
    statusId === FieldDesignChangeStatus.PendingLevelConcurrences.id
  const tagStyle = {
    maxWidth: renderTags ? 150 : 100,
    whiteSpace: 'normal',
  } as const
  if (statusId === FieldDesignChangeStatus.Draft.id) return null
  return (
    <Spin spinning={metadata?.loading}>
      {renderTags ? (
        <CIMSTags
          tags={groups.map(({ name, description }) => ({
            style: tagStyle,
            text: name,
            tooltip: description,
          }))}
        />
      ) : (
        <CIMSChecklist
          items={groups.map(
            ({ id: approvalGroupId, name, description, checked, by }) => {
              const can = approve.can.get(approvalGroupId)
              return {
                key: `${id}_${approvalGroupId}`,
                tag: { children: name, tooltip: description },
                checked,
                disabled: [
                  {
                    disabled: deleted,
                    reason: { why: 'inactive', entityName: 'FDC' },
                  },
                  { disabled: !can, reason: 'missing permission' },
                  {
                    disabled:
                      statusId !== FieldDesignChangeStatus.PendingApprovals.id,
                    reason: {
                      why: 'only status',
                      statusName: FieldDesignChangeStatus.PendingApprovals.name,
                    },
                  },
                  {
                    disabled: !resolution,
                    reason: {
                      why: 'disabled',
                      message:
                        'A Resolution must be entered before Approvals can be made',
                    },
                  },
                  // If already disabled, and checked, show who approved
                  {
                    disabled:
                      (!can ||
                        deleted ||
                        !resolution ||
                        statusId !==
                          FieldDesignChangeStatus.PendingApprovals.id) &&
                      checked,
                    reason: { why: 'disabled', message: `Approved by ${by}` },
                  },
                ],
                onChange: async () => {
                  await approve.fn({
                    variables: {
                      input: {
                        approvalGroupId,
                        fieldDesignChangeId: id,
                        approved: !checked,
                      },
                    },
                  })
                  await fieldDesignChanges?.refetch()
                },
              }
            }
          )}
        />
      )}
    </Spin>
  )
}

export default ApprovalGroups
